import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { ArrowLeft, ArrowRight, Shield, Smartphone, Hand } from "lucide-react";
import { observer } from "mobx-react-lite";
import { directusService } from "@/services/directus";
import { usePaymentTracking } from "@/hooks/usePaymentTracking";
import { useCoreController } from "@/services/coreController";
import {
  getOperatorFromPrefix,
  OperatorConfig,
  AppConstants,
} from "@/constants/theme";
import { storage } from "@/lib/storage";
import { analyticsService } from "@/services/analytics";
import { Notification } from "@/components/Notification";

interface PaymentPageState {
  type: "deposit";
  amount: number;
  userId: string;
  userName: string;
  accountId: string;
  accountType: string;
  commercialId?: string;
}

export const PaymentProcessingPage = observer(function PaymentProcessingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const coreController = useCoreController();
  const state = location.state as PaymentPageState;

  // States
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isPaymentCreated, setIsPaymentCreated] = useState(false);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(
    storage.read<string>("pay_with") || ""
  );
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    AppConstants.sessionDuration
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: 'error' | 'success';
  }>({
    isVisible: false,
    message: '',
    type: 'error',
  });

  const timerRef = useRef<number | null>(null);

  // WebSocket tracking
  const { trackingStatus, isPollingMode, isCompleted } = usePaymentTracking({
    paymentIntentId: paymentIntentId || "",
    apiKey: coreController.appSettingResponse.apiKey || "",
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError,
  });

  useEffect(() => {
    if (!state) {
      navigate("/");
      return;
    }

    analyticsService.trackScreenView("payment_processing_page");

    // Simulate initialization time (3 seconds like Flutter)
    const timer = setTimeout(() => {
      setIsInitialized(true);
      startSessionTimer();
    }, 3000);

    return () => {
      clearTimeout(timer);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [state]);

  const startSessionTimer = () => {
    timerRef.current = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetTimer = () => {
    setRemainingSeconds(AppConstants.sessionDuration);
  };

  const showNotification = (message: string, type: 'error' | 'success' = 'error') => {
    setNotification({
      isVisible: true,
      message,
      type,
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const handleTimeout = () => {
    showNotification("Votre session de paiement a expiré");
    setTimeout(() => {
      navigate("/");
    }, 2000);
  };

  async function handlePhoneVerification() {
    if (!phoneNumber || phoneNumber.length !== 8) {
      showNotification("Veuillez entrer un numéro valide");
      return;
    }

    const prefix = phoneNumber.substring(0, 2);
    const operator = getOperatorFromPrefix(prefix);

    if (!operator) {
      showNotification("Veuillez entrer un numéro valide Orange ou Moov");
      return;
    }

    // Debug: Log current state
    console.debug("🔍 [PaymentProcessing] Current CoreController state:", {
      loading: coreController.loading,
      error: coreController.error,
      hasApiKey: !!coreController.appSettingResponse.apiKey,
      apiKey: coreController.appSettingResponse.apiKey,
      idFtxOrange: coreController.appSettingResponse.idFtxOrange,
      idFtxMoov: coreController.appSettingResponse.idFtxMoov,
      compteId: coreController.compte.id,
    });

    // Vérifier que les paramètres de l'app sont chargés
    if (!coreController.appSettingResponse.apiKey) {
      console.error("❌ [PaymentProcessing] API Key is missing!");
      showNotification("Configuration non chargée. Veuillez patienter ou rafraîchir la page");
      return;
    }

    setIsProcessing(true);

    try {
      storage.write("pay_with", phoneNumber);

      const appId =
        operator === "orange"
          ? coreController.appSettingResponse.idFtxOrange
          : coreController.appSettingResponse.idFtxMoov;

      const commercialId = coreController.resolveCommercialId(
        state.commercialId
      );
      console.debug("📍 [Payment] commercial_id for intent:", commercialId);

      const response = await directusService.createPaymentIntent({
        app_id: appId!,
        mob_app_id: coreController.compte.id,
        amount: state.amount,
        phonenumber: phoneNumber,
        userID: state.userId, // Utiliser state.userId au lieu de state.accountId
        commercial_id: commercialId,
      });

      if (!response.paymentIntent) {
        throw new Error("Payment intent is null from API response");
      }

      setPaymentIntentId(response.paymentIntent);
      setIsPhoneVerified(true);
      setIsPaymentCreated(true);

      analyticsService.trackEvent("payment_intent_created", {
        amount: state.amount,
        operator,
      });
    } catch (error: any) {
      console.error("Error creating payment intent:", error);

      const errorMessage =
        error.response?.data?.error || "Une erreur s'est produite";
      showNotification(errorMessage);

      analyticsService.trackError(error, "payment_intent_creation");
    } finally {
      setIsProcessing(false);
    }
  }

  function handlePaymentSuccess() {
    setIsPaymentCompleted(true);
    setShowConfetti(true);

    analyticsService.trackEvent("payment_success", {
      amount: state.amount,
      paymentMethod: "mobile_money",
      transactionType: "deposit",
      transactionId: paymentIntentId || undefined,
    });

    showNotification("Opération terminée avec succès!", "success");

    setTimeout(() => {
      navigate("/");
    }, 2000);
  }

  function handlePaymentError(error: Error) {
    showNotification("Le paiement n'a pas été détecté. Veuillez vérifier votre compte ou réessayer.");

    analyticsService.trackEvent("payment_failure", {
      amount: state.amount,
      reason: error.message,
      transactionType: "deposit",
    });

    setTimeout(() => {
      navigate("/");
      navigate("/");
    }, 5000);
  }

  const getOperator = () => {
    if (!phoneNumber || phoneNumber.length < 2) return null;
    const prefix = phoneNumber.substring(0, 2);
    return getOperatorFromPrefix(prefix);
  };

  const operator = getOperator();
  const operatorConfig = operator ? OperatorConfig[operator] : null;

  if (!state) {
    return null;
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const timerColor = remainingSeconds <= 60 ? "#ef4444" : "#002366";

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={400}
        />
      )}

      {/* App Bar - Exact Flutter Style */}
      <div className="bg-transparent px-4 py-4 flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 bg-white rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
        >
          <ArrowLeft className="w-5 h-5 text-[#002366]" />
        </button>
        <h1
          className="flex-1 text-center text-lg font-bold text-[#002366]"
          style={{ fontFamily: "Poppins, sans-serif" }}
        >
          Confirmation du paiement
        </h1>
        <div className="w-9" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Loading State - Exact Flutter Style */}
        {!isInitialized && (
          <div className="flex-1 flex flex-col items-center justify-center p-5">
            <div className="flex gap-2 mb-8">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-4 h-4 rounded-full bg-[#002366]"
                  animate={{
                    y: [0, -15, 0],
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: i * 0.15,
                  }}
                />
              ))}
            </div>
            <p
              className="text-lg font-medium text-[#002366] mb-2"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Chargement du système de paiement...
            </p>
            <p
              className="text-sm text-gray-600 text-center px-4"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Veuillez patienter pendant que nous initialisons votre session
              sécurisée
            </p>
          </div>
        )}

        {/* Main Content After Init */}
        {isInitialized && (
          <div className="flex-1 flex flex-col">
            {/* Timer - Exact Flutter Style */}
            <div className="flex justify-center px-4 py-2">
              <div
                className="inline-flex items-center px-4 py-2 rounded-xl border"
                style={{
                  backgroundColor:
                    remainingSeconds <= 60 ? "#ff00001A" : "#0023661A",
                  borderColor:
                    remainingSeconds <= 60 ? "#ff00004D" : "#0023664D",
                }}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke={timerColor}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  className="text-xs text-gray-700 font-medium"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                >
                  Session expire dans :{" "}
                </span>
                <span
                  className="text-sm font-bold ml-1"
                  style={{
                    color: timerColor,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {formatTime(remainingSeconds)}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Header Card - Exact Flutter Style */}
              <div
                className="rounded-[25px] p-3 shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #002366, #1a365d)",
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p
                      className="text-white/80 text-xs mb-2"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                      Montant à Payer
                    </p>
                    <p
                      className="text-white text-xl font-bold"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                      {state.amount} FCFA
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="bg-white/10 rounded-xl p-2 mt-2">
                  <p
                    className="text-white text-sm"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Dépôt de {state.amount} FCFA sur le compte {state.userId}{" "}
                    appartenant à{" "}
                    <span className="text-red-500 font-bold text-xl">
                      {state.userName}
                    </span>
                  </p>
                </div>
              </div>

              {/* Phone Form - Exact Flutter Style */}
              {!isPhoneVerified && (
                <div className="bg-white rounded-[20px] p-5 shadow-md">
                  {/* Configuration Loading Indicator */}
                  {coreController.loading && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p
                          className="text-sm text-blue-700"
                          style={{ fontFamily: "Poppins, sans-serif" }}
                        >
                          Chargement de la configuration...
                        </p>
                      </div>
                    </div>
                  )}

                  {/* API Key Missing Warning */}
                  {!coreController.loading &&
                    !coreController.appSettingResponse.apiKey && (
                      <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p
                          className="text-sm text-red-700"
                          style={{ fontFamily: "Poppins, sans-serif" }}
                        >
                          ⚠️ Configuration non chargée. Veuillez rafraîchir la
                          page.
                        </p>
                      </div>
                    )}

                  <h2
                    className="text-lg font-bold text-[#002366] mb-2"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Entrez votre numéro de téléphone
                  </h2>
                  <p
                    className="text-sm text-gray-600 mb-5"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Le numéro (ORANGE MONEY ou MOOV MONEY) que vous utiliserez
                    pour effectuer le paiement
                  </p>

                  <div className="relative mb-5">
                    <div
                      className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                      style={{ backgroundColor: "#0023661A" }}
                    >
                      <Smartphone className="w-5 h-5 text-[#002366]" />
                    </div>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        storage.write("pay_with", e.target.value);
                        resetTimer();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handlePhoneVerification();
                        }
                      }}
                      maxLength={8}
                      placeholder="ex. 76000000 ou 70000000"
                      className="w-full pl-14 pr-4 py-4 text-base border-2 rounded-xl bg-gray-50
                               border-gray-300 focus:border-[#002366] focus:outline-none"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    />
                  </div>

                  <button
                    onClick={handlePhoneVerification}
                    disabled={isProcessing || phoneNumber.length !== 8}
                    className="w-full h-[50px] bg-[#002366] hover:bg-[#001a4d] text-white rounded-xl
                             font-bold flex items-center justify-center gap-2 transition-all
                             disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    {isProcessing ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <ArrowRight className="w-5 h-5" />
                        <span>Continuer</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Payment Status - Exact Flutter Style */}
              {isPhoneVerified && operatorConfig && (
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-5 shadow-md">
                    <img
                      src={`/assets/${operator}.png`}
                      alt={operatorConfig.name}
                      className="h-[60px] object-contain mx-auto rounded-2xl mb-4"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />

                    <p
                      className="text-center text-base font-bold text-[#002366] mb-4"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                      {isPaymentCompleted
                        ? "Paiement Validé!"
                        : "Complétez le paiement et votre recharge sera traitée automatiquement"}
                    </p>

                    {/* Tracking Status - Exact Flutter Style */}
                    {isPaymentCreated && !isPaymentCompleted && (
                      <div
                        className="p-3 rounded-xl border"
                        style={{
                          backgroundColor: isPollingMode
                            ? "#ff99001A"
                            : "#3b82f61A",
                          borderColor: isPollingMode
                            ? "#ff99004D"
                            : "#3b82f64D",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-5 h-5 border-2 border-current rounded-full animate-spin"
                            style={{
                              borderTopColor: "transparent",
                              color: isPollingMode ? "#ff9900" : "#3b82f6",
                            }}
                          />
                          <p
                            className="text-sm font-semibold"
                            style={{
                              color: isPollingMode ? "#92400e" : "#1e40af",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          >
                            {trackingStatus}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* USSD Card - Exact Flutter Style */}
                  <div className="bg-white rounded-xl p-4 shadow-md">
                    <div className="flex items-center gap-2 text-gray-600 mb-3">
                      <Hand className="w-4 h-4" />
                      <span
                        className="text-xs"
                        style={{ fontFamily: "Poppins, sans-serif" }}
                      >
                        Tapez pour lancer la transaction
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        const ussdCode = operatorConfig.ussdCode(state.amount);
                        window.location.href = `tel:${encodeURIComponent(
                          ussdCode
                        )}`;
                        resetTimer();
                      }}
                      className="w-full bg-gray-50 rounded-lg border border-gray-200 p-3
                               hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <p
                          className="text-base font-semibold text-gray-800 tracking-wide"
                          style={{ fontFamily: "Poppins, sans-serif" }}
                        >
                          {operatorConfig.ussdCode(state.amount)}
                        </p>
                        <div
                          className="p-2 rounded-full"
                          style={{
                            backgroundColor: `${operatorConfig.color}1A`,
                          }}
                        >
                          <Smartphone
                            className="w-[18px] h-[18px]"
                            style={{ color: operatorConfig.color }}
                          />
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Security Footer - Exact Flutter Style */}
      <div className="bg-white rounded-t-[30px] shadow-[0_-5px_10px_rgba(0,0,0,0.05)] p-3">
        <div className="flex items-center justify-center gap-3">
          <div className="p-2 bg-green-100 rounded-full">
            <Shield className="w-5 h-5 text-green-600" />
          </div>
          <div
            className="text-base"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            <span className="text-gray-600">Secured by </span>
            <span className="font-bold text-[#002366]">Futurix</span>
            <span className="font-bold text-green-600"> Pay</span>
          </div>
        </div>
      </div>

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
});

export default PaymentProcessingPage;
